import { useSelector } from 'react-redux'
import { useMemo } from 'react'
import moment from 'moment'

import { ApplicationState } from 'src/store'
import {
  Agency,
  Request,
  RequestQuoteVehicle,
  RequestTicketUserData,
  RequestTicketSelectedSituation
} from 'src/types'

export type SelectedVehicle = Pick<
  RequestQuoteVehicle,
  | 'air_conditioning'
  | 'company_image_url'
  | 'company'
  | 'gearbox'
  | 'information'
  | 'model'
  | 'request_quote_vehicle_unique'
  | 'return_location'
  | 'total_value'
  | 'vehicle_image_url'
  | 'withdrawal_location'
> & {
  agency_data?: Agency
  eticket_code?: string
  return_date: string
  return_time: string
  situation: RequestTicketSelectedSituation
  status?: any
  user_data: RequestTicketUserData[]
  withdrawal_date: string
  withdrawal_time: string
}

function useGetSelectedVehicles(request: Request): SelectedVehicle[] {
  const {
    vehicles: { list }
  } = useSelector((state: ApplicationState) => ({
    vehicles: state.vehicles
  }))

  const selectedVehicles = useMemo<SelectedVehicle[]>(() => {
    const items: SelectedVehicle[] = []

    if (!request?.request_id) {
      const selectedVehicles: SelectedVehicle[] = []

      request.full_request_vehicle.forEach(fullRequestVehicleItem => {
        const selectedVehicle = list.find(
          findItem =>
            findItem.request_quote_vehicle_hash ===
            fullRequestVehicleItem.request_quote_vehicle_hash[0]
        )

        const selectedValue = selectedVehicle.values.find(
          findItem =>
            findItem.request_quote_vehicle_unique ===
            fullRequestVehicleItem.request_quote_vehicle_unique[0]
        )

        if (selectedValue) {
          selectedVehicles.push({
            agency_data: null,
            air_conditioning: selectedVehicle.air_conditioning,
            company_image_url: selectedVehicle.company_image_url,
            company: selectedVehicle.company,
            gearbox: selectedVehicle.gearbox,
            information: selectedVehicle.information,
            model: selectedVehicle.model,
            situation: null,
            request_quote_vehicle_unique:
              selectedVehicle.request_quote_vehicle_unique,
            return_date: moment
              .unix(selectedVehicle.return_date)
              .utc()
              .format('DD/MM/YYYY'),
            return_time: moment
              .unix(selectedVehicle.return_date)
              .utc()
              .format('HH:mm'),
            return_location: selectedVehicle.return_location,
            total_value: selectedVehicle.total_value,
            user_data:
              fullRequestVehicleItem.travelers.map<RequestTicketUserData>(
                guestItem => {
                  const find = request.request_user_data.find(
                    findItem => findItem.traveler.user_id === guestItem.user_id
                  )

                  return {
                    request_ticket_user_data_id: null,
                    request_ticket_id: null,
                    included: find.included,
                    owner: false,
                    traveler: find.traveler,
                    user_id: find.user_id
                  }
                }
              ),

            vehicle_image_url: selectedVehicle.vehicle_image_url,
            withdrawal_date: moment
              .unix(selectedVehicle.withdrawal_date)
              .utc()
              .format('DD/MM/YYYY'),
            withdrawal_time: moment
              .unix(selectedVehicle.withdrawal_date)
              .utc()
              .format('HH:mm'),
            withdrawal_location: selectedVehicle.withdrawal_location
          })
        }
      })

      return selectedVehicles
    }
    request.request_tickets
      .filter(
        requestTicketItem =>
          requestTicketItem.class_type === 'SERVICE' &&
          requestTicketItem.request_ticket_type === 'VEHICLE'
      )
      .forEach(requestTicketItem => {
        const {
          request_ticket_user_data,
          request_ticket_vehicle_selected: {
            eticket_code,
            request_quote_vehicle: {
              air_conditioning,
              company_image_url,
              company,
              gearbox,
              information,
              model,
              request_quote_vehicle_unique,
              request_quote,
              return_date,
              return_location,
              vehicle_image_url,
              withdrawal_date,
              withdrawal_location
            },
            situation
          },
          status
        } = requestTicketItem

        const receiptCost = requestTicketItem.request_ticket_receipts[0].cost

        items.push({
          agency_data: request_quote.customer_supplier.supplier.agency,
          air_conditioning,
          company_image_url,
          company,
          eticket_code,
          gearbox,
          information,
          model,
          situation,
          request_quote_vehicle_unique,
          return_date: moment.unix(return_date).utc().format('DD/MM/YYYY'),
          return_location,
          return_time: moment.unix(return_date).utc().format('HH:mm'),
          status,
          total_value: receiptCost,
          user_data: request_ticket_user_data
            .filter(filter => filter.included)
            .sort((a, b) => (a.owner ? -1 : b.owner ? 1 : 0)),
          vehicle_image_url,
          withdrawal_date: moment
            .unix(withdrawal_date)
            .utc()
            .format('DD/MM/YYYY'),
          withdrawal_location,
          withdrawal_time: moment.unix(withdrawal_date).utc().format('HH:mm')
        })
      })

    return items
  }, [list, request])
  return selectedVehicles
}

export default useGetSelectedVehicles
